import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarCheck, CalendarClock, CheckCircle2, XCircle, IndianRupee, Wallet, Percent, Search, X, Mail, Phone, FileDown, PieChart, CalendarPlus,
} from 'lucide-react';
import { getMyGym } from '../api/gymApi';
import { getMyGymBookings } from '../api/bookingApi';
import { useToast } from '../components/ToastProvider';
import StatCard from '../components/StatCard';
import DonutChart from '../components/DonutChart';
import { downloadCsv } from '../lib/exportCsv';
import { formatTime12h, todayLocalDateStr } from '../utils/helpers';

const PAGE_SIZE = 6;
const EXPORT_LIMIT = 50;
const EMPTY_STATS = { total: 0, upcoming: 0, completed: 0, cancelled: 0, revenue: 0 };

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
];

const STATUS_META = {
  upcoming: { label: 'Upcoming', className: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' },
  completed: { label: 'Completed', className: 'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300' },
  cancelled: { label: 'Cancelled', className: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300' },
};

function displayStatus(b, todayKey) {
  if (b.status === 'cancelled') return 'cancelled';
  return b.date < todayKey ? 'completed' : 'upcoming';
}

export default function BookingsPage() {
  const { showToast } = useToast();
  const todayKey = todayLocalDateStr();

  const [gym, setGym] = useState(null);
  const [gymLoading, setGymLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    getMyGym().then(setGym).catch(() => setGym(null)).finally(() => setGymLoading(false));
  }, []);

  // Debounce free-text search so every keystroke doesn't fire a request.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);

  const listParams = {
    tab: filter === 'all' ? undefined : filter,
    search: debouncedQuery || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  };

  useEffect(() => {
    if (gymLoading || !gym) { setLoading(false); return; }
    setFetching(true);
    getMyGymBookings({ ...listParams, page, limit: PAGE_SIZE })
      .then((data) => {
        setBookings(Array.isArray(data?.bookings) ? data.bookings : []);
        setPagination(data?.pagination || { page: 1, totalPages: 1 });
        setStats({ ...EMPTY_STATS, ...(data?.stats || {}) });
      })
      .catch(() => {
        setBookings([]);
        setPagination({ page: 1, totalPages: 1 });
        setStats(EMPTY_STATS);
      })
      .finally(() => { setLoading(false); setFetching(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gym, gymLoading, filter, debouncedQuery, dateFrom, dateTo, page]);

  const selectFilter = useCallback((f) => { setFilter(f); setPage(1); }, []);
  const clearDateRange = () => { setDateFrom(''); setDateTo(''); setPage(1); };

  async function handleExport() {
    setExporting(true);
    try {
      const data = await getMyGymBookings({ ...listParams, page: 1, limit: EXPORT_LIMIT });
      const rows = data?.bookings || [];
      downloadCsv('bookings.csv', [
        { key: 'customerName', label: 'Customer' },
        { key: 'customerEmail', label: 'Email' },
        { key: 'customerPhone', label: 'Phone' },
        { key: 'date', label: 'Date' },
        { key: 'startTime', label: 'Start' },
        { key: 'endTime', label: 'End' },
        { key: 'bookingId', label: 'Reference' },
        { key: 'status', label: 'Status' },
      ], rows);
      const total = data?.pagination?.total || rows.length;
      showToast(total > EXPORT_LIMIT
        ? `Exported the first ${EXPORT_LIMIT} of ${total} matching bookings.`
        : `Exported ${rows.length} booking${rows.length === 1 ? '' : 's'} to CSV.`);
    } catch {
      showToast('Failed to export bookings.', 'error');
    } finally {
      setExporting(false);
    }
  }

  if (gymLoading || loading) {
    return (
      <div className="flex items-center justify-center py-40">
        <div className="w-10 h-10 rounded-full border-4 border-[#e8f0fe] dark:border-[#1a2e4c] border-t-[#1a73e8] animate-spin" />
      </div>
    );
  }

  if (!gym) {
    return (
      <div className="max-w-md mx-auto text-center py-16">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-[#e8f0fe] dark:bg-[#1a2e4c] text-[#1a73e8]">
          <CalendarPlus className="w-7 h-7" />
        </div>
        <h3 className="font-semibold text-base text-[#202124] dark:text-[#e8eaed]">No gym listing yet</h3>
        <p className="text-sm text-[#5f6368] dark:text-[#9aa0a6] mt-1">Create your gym first to start receiving bookings.</p>
        <Link to="/my-gym" className="inline-block mt-4 text-sm font-semibold text-white px-5 py-2.5 rounded-full bg-[#1a73e8] hover:bg-[#1557b0]">
          Create Gym Listing
        </Link>
      </div>
    );
  }

  const resolved = stats.completed + stats.cancelled;
  const completionRate = resolved > 0 ? Math.round((stats.completed / resolved) * 100) : 0;
  const cancellationRate = resolved > 0 ? Math.round((stats.cancelled / resolved) * 100) : 0;
  const avgBookingValue = stats.upcoming + stats.completed > 0 ? Math.round(stats.revenue / (stats.upcoming + stats.completed)) : 0;

  const statusBreakdown = [
    { key: 'Completed', value: stats.completed, color: '#34A853' },
    { key: 'Cancelled', value: stats.cancelled, color: '#EA4335' },
    { key: 'Upcoming', value: stats.upcoming, color: '#1a73e8' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-[#202124] dark:text-[#e8eaed]">Bookings</h2>
        <button onClick={handleExport} disabled={exporting}
          className="px-3.5 py-1.5 rounded-full text-xs font-semibold border border-[#dadce0] dark:border-[#3c4043] text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#f1f3f4] dark:hover:bg-[#303134] cursor-pointer flex items-center gap-1.5 shrink-0 disabled:opacity-60">
          <FileDown className="w-3.5 h-3.5" /> {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CalendarCheck} label="Total Bookings" value={stats.total} tone="blue" />
        <StatCard icon={CheckCircle2} label="Completed" value={stats.completed} tone="green" />
        <StatCard icon={XCircle} label="Cancelled" value={stats.cancelled} tone="red" />
        <StatCard icon={CalendarClock} label="Upcoming" value={stats.upcoming} tone="purple" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={IndianRupee} label="Revenue" value={`₹${stats.revenue.toLocaleString('en-IN')}`} sublabel="upcoming + completed, at daily rate" tone="green" />
        <StatCard icon={Wallet} label="Avg. Value per Booking" value={`₹${avgBookingValue.toLocaleString('en-IN')}`} tone="blue" />
        <StatCard icon={Percent} label="Completion Rate" value={`${completionRate}%`} sublabel="of resolved bookings" tone="green" />
        <StatCard icon={Percent} label="Cancellation Rate" value={`${cancellationRate}%`} sublabel="of resolved bookings" tone="red" />
      </div>

      <div className="google-card p-4 sm:p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043] flex flex-wrap items-center gap-5">
        <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] flex items-center gap-1.5 shrink-0">
          <PieChart className="w-4 h-4 text-[#1a73e8]" /> Status Breakdown
        </h3>
        <DonutChart segments={statusBreakdown} size={56} strokeWidth={10} />
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          {statusBreakdown.map((s) => (
            <div key={s.key} className="flex items-center gap-1.5 text-xs">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
              <span className="text-[#5f6368] dark:text-[#9aa0a6]">{s.key}</span>
              <span className="font-bold text-[#202124] dark:text-[#e8eaed]">{s.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="inline-flex p-1 bg-[#f1f3f4] dark:bg-[#202124] rounded-full">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => selectFilter(f.id)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                filter === f.id ? 'bg-white dark:bg-[#303134] text-[#1a73e8] shadow-sm' : 'text-[#5f6368] dark:text-[#9aa0a6]'
              }`}>
              {f.label}
            </button>
          ))}
        </div>

        <div className="google-input-box px-3 py-2 flex items-center gap-2 sm:max-w-xs w-full">
          <Search className="w-4 h-4 text-[#9aa0a6] shrink-0" />
          <input type="text" placeholder="Search name or ref code..." value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            className="w-full bg-transparent text-xs font-medium outline-none text-[#202124] dark:text-[#e8eaed]" />
          {query && (
            <button onClick={() => setQuery('')} className="shrink-0 text-[#9aa0a6] hover:text-[#202124] dark:hover:text-white cursor-pointer">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="google-input-box px-3 py-2 flex items-center gap-2">
            <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="bg-transparent text-xs font-medium outline-none text-[#202124] dark:text-[#e8eaed] cursor-pointer" />
          </div>
          <span className="text-xs text-[#9aa0a6]">to</span>
          <div className="google-input-box px-3 py-2 flex items-center gap-2">
            <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="bg-transparent text-xs font-medium outline-none text-[#202124] dark:text-[#e8eaed] cursor-pointer" />
          </div>
          {(dateFrom || dateTo) && (
            <button onClick={clearDateRange} className="text-[#9aa0a6] hover:text-[#202124] dark:hover:text-white cursor-pointer">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="space-y-3" style={{ opacity: fetching ? 0.5 : 1, transition: 'opacity 150ms' }}>
        {bookings.length === 0 ? (
          <div className="google-card p-10 text-center bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043] text-sm text-[#9aa0a6]">
            No bookings match your filters.
          </div>
        ) : (
          bookings.map((b) => {
            const status = displayStatus(b, todayKey);
            const sm = STATUS_META[status];
            return (
              <div key={b._id} className="google-card p-4 sm:p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed]">{b.customerName}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${sm.className}`}>{sm.label}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap text-[11px] text-[#5f6368] dark:text-[#9aa0a6] mt-1">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {b.customerEmail}</span>
                      {b.customerPhone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {b.customerPhone}</span>}
                    </div>
                    <div className="text-xs text-[#5f6368] dark:text-[#9aa0a6] mt-1">
                      {b.date} · {formatTime12h(b.startTime)}–{formatTime12h(b.endTime)}
                      {b.bookingId && <> · <span className="font-mono font-semibold">#{b.bookingId}</span></>}
                    </div>
                  </div>
                  <span className="text-sm font-bold text-[#202124] dark:text-[#e8eaed] shrink-0">₹{gym.dailyPassPrice}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pagination.page === 1}
            className="px-3 py-1.5 rounded-full text-xs font-semibold border border-[#dadce0] dark:border-[#3c4043] text-[#5f6368] dark:text-[#9aa0a6] disabled:opacity-40 cursor-pointer hover:bg-[#f1f3f4] dark:hover:bg-[#303134]">
            Prev
          </button>
          <span className="text-xs text-[#5f6368] dark:text-[#9aa0a6]">Page {pagination.page} of {pagination.totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))} disabled={pagination.page === pagination.totalPages}
            className="px-3 py-1.5 rounded-full text-xs font-semibold border border-[#dadce0] dark:border-[#3c4043] text-[#5f6368] dark:text-[#9aa0a6] disabled:opacity-40 cursor-pointer hover:bg-[#f1f3f4] dark:hover:bg-[#303134]">
            Next
          </button>
        </div>
      )}
    </div>
  );
}
